/**
 * Timecode Utility Functions
 *
 * Pure functions for calculating timecode-based pattern and chord correlations.
 * These functions determine which chord should play at each pattern step based on
 * independent timing configurations for patterns and chords.
 */

import {
  PatternGeneration,
  ChordTiming,
} from "../components/sequencer/types";

/**
 * Calculate the duration of one bar in seconds based on BPM.
 * One bar = 4 beats at the given BPM.
 */
export function getBarDuration(bpm: number): number {
  const beatsPerSecond = bpm / 60;
  return 4 / beatsPerSecond; // 4 beats per bar
}

/**
 * Calculate the total duration of the pattern sequence in seconds.
 */
export function getPatternDuration(
  config: PatternGeneration,
  bpm: number
): number {
  const barDuration = getBarDuration(bpm);
  // Pattern duration is affected by bars and rate
  // Rate > 1 means faster (shorter duration), rate < 1 means slower (longer duration)
  return (config.bars * barDuration) / config.rate;
}

/**
 * Calculate the total duration of the chord sequence in seconds.
 */
export function getChordDuration(chordTiming: ChordTiming, bpm: number): number {
  const barDuration = getBarDuration(bpm);
  return chordTiming.bars * barDuration;
}

/**
 * Calculate the time position of a specific pattern step within one pattern loop.
 * Returns time in seconds from the start of the pattern.
 */
export function getPatternStepTime(
  stepIndex: number,
  config: PatternGeneration,
  bpm: number
): number {
  const patternDuration = getPatternDuration(config, bpm);
  // Each step is evenly distributed across the pattern duration
  return (stepIndex / config.steps) * patternDuration;
}

/**
 * Calculate the absolute time position of a pattern step, accounting for pattern loops.
 * This gives the time in the master timeline (the longer of pattern or chord duration).
 *
 * @param stepIndex - The step index (can be >= config.steps for subsequent loops)
 * @param config - Pattern generation config
 * @param bpm - Beats per minute
 * @returns Time in seconds from the start of playback
 */
export function getAbsoluteStepTime(
  stepIndex: number,
  config: PatternGeneration,
  bpm: number
): number {
  const patternDuration = getPatternDuration(config, bpm);
  const loopIndex = Math.floor(stepIndex / config.steps);
  const stepInLoop = stepIndex % config.steps;
  const stepTimeInLoop = (stepInLoop / config.steps) * patternDuration;
  return loopIndex * patternDuration + stepTimeInLoop;
}

/**
 * Get which chord is active at a given time position within the chord loop.
 * Uses the ribbon positions (percentages) to determine chord boundaries.
 *
 * @param time - Time in seconds from the start of the chord loop
 * @param chordTiming - Chord timing configuration with ribbon positions
 * @param chords - Array of chord names (roman numerals)
 * @returns The chord that should be playing at the given time
 */
export function getChordAtTime(
  time: number,
  chordTiming: ChordTiming,
  chords: string[]
): string {
  const barDuration = getBarDuration(120);
  const actualChordDuration = chordTiming.bars * barDuration;

  // Normalize time to be within one chord loop
  const normalizedTime = time % actualChordDuration;

  // Convert time to percentage of chord loop
  const timePercent = normalizedTime / actualChordDuration;

  // Find which ribbon (chord) contains this time percentage
  for (let i = 0; i < chordTiming.ribbonPositions.length; i++) {
    const ribbon = chordTiming.ribbonPositions[i];
    if (timePercent >= ribbon.startPercent && timePercent < ribbon.endPercent) {
      return chords[i % chords.length];
    }
  }

  // Edge case: if timePercent === 1 (exactly at the end), return the last chord
  if (timePercent >= 1) {
    return chords[chords.length - 1];
  }

  // Fallback: return the first chord
  return chords[0];
}

/**
 * Get the index of the chord that is active at a given time position.
 *
 * @param time - Time in seconds from the start of playback
 * @param chordTiming - Chord timing configuration with ribbon positions
 * @param bpm - Beats per minute
 * @returns The index of the chord that should be playing at the given time
 */
export function getChordIndexAtTime(
  time: number,
  chordTiming: ChordTiming,
  bpm: number
): number {
  const chordDuration = getChordDuration(chordTiming, bpm);

  // Normalize time to be within one chord loop
  const normalizedTime = time % chordDuration;

  // Convert time to percentage of chord loop
  const timePercent = normalizedTime / chordDuration;

  // Find which ribbon (chord) contains this time percentage
  for (let i = 0; i < chordTiming.ribbonPositions.length; i++) {
    const ribbon = chordTiming.ribbonPositions[i];
    if (timePercent >= ribbon.startPercent && timePercent < ribbon.endPercent) {
      return i;
    }
  }

  // Edge case: if timePercent >= 1 (at or past the end), return the last chord index
  if (timePercent >= 1) {
    return chordTiming.ribbonPositions.length - 1;
  }

  // Fallback: return first chord
  return 0;
}

/**
 * Get which chord is active at a given time position, using the actual BPM.
 *
 * @param time - Time in seconds from the start of playback
 * @param chordTiming - Chord timing configuration with ribbon positions
 * @param chords - Array of chord names (roman numerals)
 * @param bpm - Beats per minute
 * @returns The chord that should be playing at the given time
 */
export function getChordAtTimeWithBpm(
  time: number,
  chordTiming: ChordTiming,
  chords: string[],
  bpm: number
): string {
  const chordIndex = getChordIndexAtTime(time, chordTiming, bpm);
  return chords[chordIndex % chords.length];
}

/**
 * Calculate which chord should play for a specific pattern step.
 * This is the main correlation function that bridges pattern and chord timing.
 *
 * @param stepIndex - The step index within the current pattern loop (0 to steps-1)
 * @param patternConfig - Pattern generation configuration
 * @param chordTiming - Chord timing configuration
 * @param chords - Array of chord names (roman numerals)
 * @param bpm - Beats per minute
 * @returns The chord that should be played at this step
 */
export function getChordForPatternStep(
  stepIndex: number,
  patternConfig: PatternGeneration,
  chordTiming: ChordTiming,
  chords: string[],
  bpm: number
): string {
  // Calculate the time position of this step in the absolute timeline
  const stepTime = getPatternStepTime(stepIndex, patternConfig, bpm);

  // Get the chord at this time position
  return getChordAtTimeWithBpm(stepTime, chordTiming, chords, bpm);
}

/**
 * Calculate the master loop duration (the longer of pattern or chord duration).
 * The shorter sequence will loop until the longer one completes.
 */
export function getMasterLoopDuration(
  patternConfig: PatternGeneration,
  chordTiming: ChordTiming,
  bpm: number
): number {
  const patternDuration = getPatternDuration(patternConfig, bpm);
  const chordDuration = getChordDuration(chordTiming, bpm);
  return Math.max(patternDuration, chordDuration);
}

/**
 * Calculate the number of times the pattern will loop during one master loop.
 */
export function getPatternLoopCount(
  patternConfig: PatternGeneration,
  chordTiming: ChordTiming,
  bpm: number
): number {
  const patternDuration = getPatternDuration(patternConfig, bpm);
  const masterDuration = getMasterLoopDuration(patternConfig, chordTiming, bpm);
  return Math.ceil(masterDuration / patternDuration);
}

/**
 * Calculate the number of times the chord sequence will loop during one master loop.
 */
export function getChordLoopCount(
  patternConfig: PatternGeneration,
  chordTiming: ChordTiming,
  bpm: number
): number {
  const chordDuration = getChordDuration(chordTiming, bpm);
  const masterDuration = getMasterLoopDuration(patternConfig, chordTiming, bpm);
  return Math.ceil(masterDuration / chordDuration);
}

/**
 * Get all chords that will be played during one master loop, indexed by absolute step.
 * Useful for visualizing the entire sequence.
 *
 * @param patternConfig - Pattern generation configuration
 * @param chordTiming - Chord timing configuration
 * @param chords - Array of chord names (roman numerals)
 * @param bpm - Beats per minute
 * @returns Map of absolute step index to chord name
 */
export function getChordSchedule(
  patternConfig: PatternGeneration,
  chordTiming: ChordTiming,
  chords: string[],
  bpm: number
): Map<number, string> {
  const schedule = new Map<number, string>();
  const patternLoops = getPatternLoopCount(patternConfig, chordTiming, bpm);
  const totalSteps = patternLoops * patternConfig.steps;

  for (let i = 0; i < totalSteps; i++) {
    const absoluteTime = getAbsoluteStepTime(i, patternConfig, bpm);
    const chord = getChordAtTimeWithBpm(absoluteTime, chordTiming, chords, bpm);
    schedule.set(i, chord);
  }

  return schedule;
}

/**
 * Convert step-based ribbon positions to percentage-based positions.
 * Used for migrating from the old step-based system.
 *
 * @param stepPositions - Array of { startStep, endStep } based on 16-step cycle
 * @returns Array of { startPercent, endPercent } in range 0-1
 */
export function stepPositionsToPercentages(
  stepPositions: Array<{ startStep: number; endStep: number }>,
  cycleLength: number = 16
): Array<{ startPercent: number; endPercent: number }> {
  return stepPositions.map((pos) => ({
    startPercent: pos.startStep / cycleLength,
    endPercent: pos.endStep / cycleLength,
  }));
}

/**
 * Convert percentage-based ribbon positions to step-based positions.
 * Used for backward compatibility with step-based visualization.
 *
 * @param percentPositions - Array of { startPercent, endPercent } in range 0-1
 * @param cycleLength - Number of steps in the cycle (default 16)
 * @returns Array of { startStep, endStep }
 */
export function percentagesToStepPositions(
  percentPositions: Array<{ startPercent: number; endPercent: number }>,
  cycleLength: number = 16
): Array<{ startStep: number; endStep: number }> {
  return percentPositions.map((pos) => ({
    startStep: Math.round(pos.startPercent * cycleLength),
    endStep: Math.round(pos.endPercent * cycleLength),
  }));
}
